import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Chart } from 'chart.js/auto';

// Funções de formatação (ajustadas para consistência)
const formatCurrency = (value) => {
    const number = parseFloat(String(value).replace(',', '.'));
    if (isNaN(number)) return "R$ 0,00";
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const formatNumber = (value, decimals = 2) => {
    const number = parseFloat(String(value).replace(',', '.'));
    if (isNaN(number)) return "0";
    return number.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

// --- FUNÇÕES PARA GERAR GRÁFICOS ---

const createRevenuePieChart = async (chartData) => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');

    return new Promise(resolve => {
        new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: chartData.labels,
                datasets: [{
                    data: chartData.data,
                    backgroundColor: ['#1E8449', '#27AE60', '#2ECC71', '#58D68D', '#82E0AA'],
                    borderColor: '#FFFFFF',
                    borderWidth: 2,
                }]
            },
            options: {
                responsive: false,
                animation: { onComplete: () => setTimeout(() => resolve(canvas.toDataURL('image/png', 1.0)), 200) },
                plugins: {
                    legend: { position: 'right', labels: { font: { size: 11, family: 'Helvetica' }, boxWidth: 15 } },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const label = context.label || '';
                                const value = context.raw || 0;
                                const total = context.chart.getDatasetMeta(0).total;
                                const percentage = ((value / total) * 100).toFixed(1);
                                return `${label}: ${formatCurrency(value)} (${percentage}%)`;
                            }
                        }
                    }
                },
                layout: { padding: 5 }
            }
        });
    });
};

const createVolumeBarChart = async (chartData) => {
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');

    return new Promise(resolve => {
        new Chart(ctx, {
            type: 'bar',
            data: {
                labels: chartData.labels,
                datasets: [{
                    label: 'Volume (m³)',
                    data: chartData.data,
                    backgroundColor: '#27AE60',
                    borderRadius: 4,
                    barThickness: 30,
                }]
            },
            options: {
                indexAxis: 'y',
                responsive: false,
                animation: { onComplete: () => setTimeout(() => resolve(canvas.toDataURL('image/png', 1.0)), 200) },
                scales: {
                    x: { title: { display: true, text: 'Volume Produzido (m³ ou st)' } },
                },
                plugins: { legend: { display: false } }
            }
        });
    });
};

/**
 * Gera um relatório em PDF moderno com os dados de desbaste selecionados.
 */
export const generateDesbasteReport = async ({ selectedData, imovelId, logo }) => {
    if (selectedData.length === 0) {
        alert("Por favor, selecione pelo menos um registro para gerar o relatório.");
        return;
    }

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const mainTextColor = '#1A2E35';
    const secondaryTextColor = '#64748b';
    const primaryColor = '#1E8449';

    // --- AGREGAÇÃO DE DADOS ---
    const totais = selectedData.reduce((acc, item) => {
        const totalLenha = item.lenha * item.preco_lenha;
        const totalToretes = item.toretes * item.preco_toretes;
        const total2025 = item.toras_20_25cm * item.preco_toras_20_25cm;
        const total2533 = item.toras_25_33cm * item.preco_toras_25_33cm;
        const total33 = item.toras_acima_33cm * item.preco_toras_acima_33cm;

        acc.arvores += parseFloat(item.arvores_cortadas || 0);
        acc.receitaTotal += totalLenha + totalToretes + total2025 + total2533 + total33;
        
        acc.volumePorCategoria['Lenha'] = (acc.volumePorCategoria['Lenha'] || 0) + parseFloat(item.lenha || 0);
        acc.volumePorCategoria['Toretes'] = (acc.volumePorCategoria['Toretes'] || 0) + parseFloat(item.toretes || 0);
        acc.volumePorCategoria['Toras 20-25cm'] = (acc.volumePorCategoria['Toras 20-25cm'] || 0) + parseFloat(item.toras_20_25cm || 0);
        acc.volumePorCategoria['Toras 25-33cm'] = (acc.volumePorCategoria['Toras 25-33cm'] || 0) + parseFloat(item.toras_25_33cm || 0);
        acc.volumePorCategoria['Toras +33cm'] = (acc.volumePorCategoria['Toras +33cm'] || 0) + parseFloat(item.toras_acima_33cm || 0);

        acc.receitaPorCategoria['Lenha'] = (acc.receitaPorCategoria['Lenha'] || 0) + totalLenha;
        acc.receitaPorCategoria['Toretes'] = (acc.receitaPorCategoria['Toretes'] || 0) + totalToretes;
        acc.receitaPorCategoria['Toras 20-25cm'] = (acc.receitaPorCategoria['Toras 20-25cm'] || 0) + total2025;
        acc.receitaPorCategoria['Toras 25-33cm'] = (acc.receitaPorCategoria['Toras 25-33cm'] || 0) + total2533;
        acc.receitaPorCategoria['Toras +33cm'] = (acc.receitaPorCategoria['Toras +33cm'] || 0) + total33;
        
        return acc;
    }, { arvores: 0, receitaTotal: 0, volumePorCategoria: {}, receitaPorCategoria: {} });

    const totalVolume = Object.values(totais.volumePorCategoria).reduce((sum, val) => sum + val, 0);

    // --- CABEÇALHO ---
    doc.addImage(logo, 'PNG', margin, 12, 35, 12);
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(mainTextColor);
    doc.text("Relatório de Desbaste", pageWidth - margin, 18, { align: 'right' });
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(secondaryTextColor);
    doc.text(`Análise Consolidada do Imóvel: ${imovelId}`, pageWidth - margin, 25, { align: 'right' });
    doc.setDrawColor(238, 242, 245);
    doc.line(margin, 35, pageWidth - margin, 35);

    let startY = 48;

    // --- KPIS ---
    const kpiData = [
        { label: "Receita Total Gerada", value: formatCurrency(totais.receitaTotal) },
        { label: "Árvores Cortadas", value: formatNumber(totais.arvores, 0) },
        { label: "Volume Total Produzido", value: `${formatNumber(totalVolume, 2)} m³/st` }
    ];
    const cardWidth = (pageWidth - (margin * 2) - 10) / 3;
    kpiData.forEach((item, index) => {
        const cardX = margin + (index * (cardWidth + 5));
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(mainTextColor);
        doc.text(item.value, cardX, startY);
        doc.setFont('Helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(secondaryTextColor);
        doc.text(item.label, cardX, startY + 5);
    });
    startY += 20;

    // --- GRÁFICOS ---
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(mainTextColor);
    doc.text("Composição da Receita", margin, startY);
    doc.text("Volume de Produção por Categoria", margin + (pageWidth / 2) - 10, startY);
    startY += 5;

    // Dados para os gráficos
    const revenueChartData = {
        labels: Object.keys(totais.receitaPorCategoria).filter(k => totais.receitaPorCategoria[k] > 0),
        data: Object.values(totais.receitaPorCategoria).filter(v => v > 0)
    };
    const volumeChartData = {
        labels: Object.keys(totais.volumePorCategoria).filter(k => totais.volumePorCategoria[k] > 0),
        data: Object.values(totais.volumePorCategoria).filter(v => v > 0)
    };

    const pieChartImg = await createRevenuePieChart(revenueChartData);
    const barChartImg = await createVolumeBarChart(volumeChartData);

    const chartHeight = 70;
    if (pieChartImg) doc.addImage(pieChartImg, 'PNG', margin - 5, startY, chartHeight, chartHeight);
    if (barChartImg) doc.addImage(barChartImg, 'PNG', margin + (pageWidth / 2) - 25, startY - 5, chartHeight * 1.5, chartHeight);
    
    startY += chartHeight + 15;

    // --- TABELA DETALHADA ---
    doc.addPage();
    startY = 25; // Reset Y para a nova página

    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(14);
    doc.text("Registros Detalhados", margin, startY);
    startY += 8;

    const head = [["Nº", "Data", "Árvores", "Volume Total", "Receita Total"]];
    const body = selectedData.map(item => {
        const totalLenha = item.lenha * item.preco_lenha;
        const totalToretes = item.toretes * item.preco_toretes;
        const total2025 = item.toras_20_25cm * item.preco_toras_20_25cm;
        const total2533 = item.toras_25_33cm * item.preco_toras_25_33cm;
        const total33 = item.toras_acima_33cm * item.preco_toras_acima_33cm;
        
        const receitaItem = totalLenha + totalToretes + total2025 + total2533 + total33;
        const volumeItem = parseFloat(item.lenha || 0) + parseFloat(item.toretes || 0) + parseFloat(item.toras_20_25cm || 0) + parseFloat(item.toras_25_33cm || 0) + parseFloat(item.toras_acima_33cm || 0);

        return [
            item.numero,
            formatDate(item.data),
            formatNumber(item.arvores_cortadas, 0),
            formatNumber(volumeItem, 2),
            formatCurrency(receitaItem)
        ];
    });

    doc.autoTable({
        head,
        body,
        startY,
        theme: 'striped',
        styles: { font: 'Helvetica', fontSize: 9, cellPadding: 3, valign: 'middle' },
        headStyles: { fillColor: primaryColor, textColor: '#FFFFFF', fontStyle: 'bold', fontSize: 10 },
        columnStyles: {
            2: { halign: 'right' },
            3: { halign: 'right' },
            4: { halign: 'right' },
        },
        didDrawPage: (data) => {
            const pageCount = doc.internal.getNumberOfPages();
            doc.setFontSize(8);
            doc.setFont('Helvetica', 'normal');
            doc.setTextColor(secondaryTextColor);
            doc.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, doc.internal.pageSize.getHeight() - 10);
            doc.text(`Página ${data.pageNumber} de ${pageCount}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
        }
    });

    doc.save(`Relatorio_Desbaste_Imovel_${imovelId}.pdf`);
};